import { memo, useEffect, useState } from 'react';
import type { BillsSummaryResponse } from '@six-pockets/shared';
import { EyeIcon, EyeOffIcon } from '@/components/icons';
import { cx } from '@/utils/cx';
import { formatINR } from '@/utils/format';
import styles from '@/styles/components/RevenueCards/style.scss';

/** How long a tapped figure stays shown. */
const REVEAL_MS = 3000;

interface RevenueCardProps {
  label: string;
  value: number;
  note: string;
  /** Hidden until tapped, then shown for 3 seconds. */
  masked: boolean;
}

function RevenueCard({ label, value, note, masked }: RevenueCardProps) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!shown) return;
    const t = setTimeout(() => setShown(false), REVEAL_MS);
    return () => clearTimeout(t);
  }, [shown]);

  if (!masked) {
    return (
      <div className={styles.card}>
        <span className={styles.label}>{label}</span>
        <span className={styles.value}>{formatINR(value)}</span>
        <span className={styles.note}>{note}</span>
      </div>
    );
  }

  // A second tap hides it again before the 3 seconds are up.
  return (
    <button
      type="button"
      className={cx(styles.card, styles.masked)}
      aria-pressed={shown}
      aria-label={shown ? `${label}: ${formatINR(value)}. Tap to hide` : `${label}: hidden. Tap to show`}
      onClick={() => setShown((s) => !s)}
    >
      <span className={styles.labelRow}>
        <span className={styles.label}>{label}</span>
        {shown ? <EyeIcon size={16} /> : <EyeOffIcon size={16} />}
      </span>
      <span className={cx(styles.value, !shown && styles.hidden)} aria-hidden>
        {shown ? formatINR(value) : '₹ ••••'}
      </span>
      <span className={styles.note} aria-hidden>
        {shown ? note : 'Tap to show'}
      </span>
    </button>
  );
}

interface RevenueCardsProps {
  summary: BillsSummaryResponse;
  /** True for logins without the reports module (the club manager): figures stay hidden until tapped. */
  masked: boolean;
}

/** Bills home: the day's table-time revenue, before and after discount (condiments not counted). */
export const RevenueCards = memo(function RevenueCards({ summary: s, masked }: RevenueCardsProps) {
  const discount = s.gross - s.net;
  return (
    <section className={styles.cards} aria-label="Table time revenue">
      <RevenueCard
        label="Gross revenue"
        value={s.gross}
        note={`Table time on ${s.bills} ${s.bills === 1 ? 'bill' : 'bills'}`}
        masked={masked}
      />
      <RevenueCard
        label="Net revenue"
        value={s.net}
        note={discount > 0 ? `After ${formatINR(discount)} discount` : 'No discounts given'}
        masked={masked}
      />
    </section>
  );
});
